import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
} from '@chakra-ui/react';
import {useGetTestLists} from '../../../../hooks/api/testList';
import Select from 'react-select';
import {ObjectId} from 'bson';
import {Controller, useForm} from 'react-hook-form';
import {useGenerateSegmentationAgent} from '../../../../hooks/api/agents';
import {useTask} from '../../../../hooks/api/tasks';
import {isGenerateAgentState} from '../../../../shared/tasks/state';
import {useQueryClient} from '@tanstack/react-query';
import {MetadataQueryKey} from '../../../../context/MetadataContext';
import {captureMessage} from '@sentry/react';
import {MinSegmentationTestListSize} from '../../../../shared/agent';
import {WizardContent} from './WizardContent';
import {useIsKeyplayAdmin} from '../../../../hooks/api/user';
import {DownloadSegmentsPromptButton} from './DownloadSegmentsPromptButton';

export const SegmentationWizard = ({
  onContinue,
}: {
  onContinue: (agentId: ObjectId) => void;
}) => {
  const [agentId, setAgentId] = useState<ObjectId | undefined>();
  const testLists = useGetTestLists({});
  const generateSegmentationAgent = useGenerateSegmentationAgent();
  const queryClient = useQueryClient();
  const isKeyplayAdmin = useIsKeyplayAdmin();

  const testListOptions = useMemo(
    () =>
      (testLists.data ?? []).map((testList) => ({
        label: testList.label,
        value: testList._id,
        isDisabled:
          testList.status.type === 'processing' ||
          testList.status.numAccounts < MinSegmentationTestListSize,
      })),
    [testLists.data]
  );

  const {isTaskInProgress, taskQuery} = useTask(
    {type: 'generateSegmentationAgent'},
    {
      enabled: !agentId,
      onTaskComplete: (task) => {
        const state = task.detailedState;
        if (!isGenerateAgentState(state) || !state.agentId) {
          captureMessage('Unknown state for generateSegmentationAgent', {
            extra: {state},
            level: 'error',
          });
          return;
        }

        setAgentId(state.agentId);

        // re-fetch customer so we have access to new agent
        queryClient.invalidateQueries({
          queryKey: [MetadataQueryKey],
        });
      },
    }
  );

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<{
    domain: string;
    testList: {
      label: string;
      value: ObjectId;
    };
  }>();

  const onGenerateAgent = handleSubmit((data) => {
    const {domain, testList} = data;
    if (!testList || !domain) {
      return;
    }

    generateSegmentationAgent.mutate({
      domain,
      testListId: testList.value,
    });
  });

  if (isTaskInProgress || generateSegmentationAgent.isPending) {
    return (
      <WizardContent
        title="Your agent is planning..."
        footer="This usually takes ~20-35 seconds"
      >
        <Spinner size="xl" color="kred.300" />
      </WizardContent>
    );
  }

  if (agentId) {
    return (
      <WizardContent
        title="Your agent is ready!"
        footer="Now you can adjust the details and test the results"
      >
        <Flex gap="4">
          <Button
            colorScheme="kbuttonblue"
            size="lg"
            onClick={() => onContinue(agentId)}
          >
            Let&apos;s Go
          </Button>
          {isKeyplayAdmin && taskQuery.data?.id && (
            <DownloadSegmentsPromptButton
              agentId={agentId}
              taskId={taskQuery.data.id}
            />
          )}
        </Flex>
      </WizardContent>
    );
  }

  return (
    <WizardContent title="Segmentation Analyst">
      <Box as="form" onSubmit={onGenerateAgent} maxW="600px" w="100%">
        <Flex direction="column" gap="10">
          <FormControl isInvalid={!!errors.testList}>
            <FormLabel>
              Select a customer list to discover your high-value segments
            </FormLabel>
            <Controller
              name={'testList'}
              control={control}
              rules={{required: true}}
              render={({field, fieldState: {error}}) => (
                <Select
                  {...field}
                  isLoading={testLists.isPending}
                  options={testListOptions}
                  styles={{
                    control: (base) => ({
                      ...base,
                      // Outline the component in red when there is an error
                      borderColor: error ? 'red' : base.borderColor,
                      boxShadow: error ? '0 0 0 1px red' : base.boxShadow,
                      '&:hover': {
                        borderColor: error ? 'red' : base.borderColor,
                      },
                    }),
                  }}
                />
              )}
            />
            <FormHelperText>
              Minimum test list size is {MinSegmentationTestListSize}.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!errors.domain}>
            <FormLabel>Enter your website URL for context</FormLabel>
            <Input
              autoComplete="off"
              bgColor="white"
              placeholder="e.g. example.com"
              {...register('domain', {
                required: true,
              })}
            />
          </FormControl>

          <Button
            colorScheme="kbuttonblue"
            onClick={(e) => {
              if (agentId) {
                onContinue(agentId);
                e.preventDefault();
              }
            }}
            type="submit"
            w="fit-content"
          >
            Generate Agent
          </Button>
        </Flex>
      </Box>
    </WizardContent>
  );
};
